<template>
    <div class="row">
        <div class="col-12 mb-4 d-flex flex-row justify-content-between align-items-center">
            <h1>{{ group.id ? $t('manage-tag-group') : $t('new-tag-group') }}</h1>
            <button
                class="btn btn-primary"
                @click="saveGroup"
                :disabled="!group.name"
            >
                {{ group.id ? $t('save') : $t('create') }}
            </button>
        </div>
        <div class="col-12 mb-4">
            <TextInput
                placeholder="Enter a name..."
                v-model="group.name"
                label="Name"
                class="mb-4"
                :error="!group.name && !loading && group.id"
            />
        </div>
        <div class="col-12 mb-4" v-if="group.id > 0">
            <label>{{  $t('tags') }}</label>
            <Table
                :data="group.tags"
                :loading="loading"
                :submitting="submitting"
                :cols="cols"
                :rowDelete="true"
                @deleteItem="openRemoveTagDialog"
            />

            <div class="d-flex flex-row align-items-center">
                <TextInput
                	:label="$t('new-tag')"
                	:srOnly="true"
                    v-model="newTag.tag_name"
                    :placeholder="$t('enter-new-tag-name')"
                    class="mb-0"
                    @enter="addTag"
                >
                    <template v-slot:append>
                        <div class="input-group-append">
                            <button class="btn btn-primary" @click="addTag">
                                <b-icon icon="plus" class="mr-1"/>{{ $t('add') }}
                            </button>
                        </div>
                    </template>
                </TextInput>
            </div>
        </div>
    </div>
</template>

<script>
import TextInput from "src/components/Inputs/TextInput.vue"
import Table from "src/components/Table/Table.vue"

export default {
    components: {
        TextInput,
        Table,
    },
    props: {
        id: {
            type: Number,
            default: 0,
        }
    },
    mounted() {
        const promise = this.fetchGroup()
        if (promise) promise.then(() => {
            this.loading = false
        })
    },
    data() {
        return {
            loading: true,
            submitting: false,
            search: '',
            group: {
                id: Number(this.$route.params.tagGroupId),
                name: '',
                reflectiononly: 1,
                tags: [],
            },
            newTag: { tag_name: '' },
            selectedTag: undefined,
            cols: [
                { key: 'id', heading: 'ID', size: 2, },
                { key: 'name', heading: 'Name', size: 8, },
            ],
        }
    },
    methods: {
        fetchGroup() {
            if (this.$route.params.tagGroupId == 0) return
            this.$store.commit('showBlockingSpinner')

            return this.$cfetch(`${this.$settings.APIBaseURL}/tags/groups/get`, {
                method: 'GET',
                data: { id: this.$route.params.tagGroupId },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error('Cannot find tag group')

                this.group = res.result
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('cannot-find-tag-form'),
                    confirmCallback: this.$router.push('/settings/taggroups'),
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
                this.loading = false
            })
        },
        saveGroup() {
            this.$store.commit('showBlockingSpinner')

            this.$cfetch(`${this.$settings.APIBaseURL}/tags/groups/save`, {
                method: 'POST',
                data: {
                    ...this.group,
                    tags: undefined,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error('Unexpected error saving tag group')

                this.group = res.result
                this.$router.push(`/settings/taggroups/${this.group.id}`)
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-saving-tag-group'),
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
            })
        },
        addTag() {
            if (
                this.submitting
                || !this.newTag.tag_name
                || this.group.tags.findIndex(tag => tag.name === this.newTag.tag_name) > -1
            ) return
            this.submitting = true

            this.$cfetch(`${this.$settings.APIBaseURL}/tags/tag`, {
                method: 'POST',
                data: {
                    ...this.newTag,
                    taggroup_id: this.group.id,
                    save: true,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error('Unexpected error saving tag')

                this.group.tags.push({
                    id: res.tag.tag_id,
                    name: res.tag.tag_name,
                    taggroup_id: res.tag.taggroup_id,
                })
                this.newTag = { tag_name: '' }
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-saving-tag'),
                })
            }).finally(() => {
                this.submitting = false
            })
        },
        openRemoveTagDialog(tag) {
            this.selectedTag = tag

            this.$store.commit('openDialog', {
                title: this.$t('are-you-sure'),
                content: this.$t('delete-tag-warning'),
                cancelBtn: true,
                confirmCallBack: this.removeTag,
            })
        },
        removeTag() {
            if (!this.selectedTag) return

            this.$cfetch(`${this.$settings.APIBaseURL}/tags/deletetag`, {
                method: 'DELETE',
                data: { id: this.selectedTag.id }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw new Error('Unexpected error deleting tag')

                this.group.tags = this.group.tags.filter(el => el.id !== this.selectedTag.id)
            }).catch(err => {
                console.error(err)
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-deleting-tag'),
                })
            }).finally(() => {
                this.selectedTag = undefined
            })
        }
    }
}
</script>
